import { useAuth0 } from '@auth0/auth0-react'
import { AlertBanner, Button, Card, useToast } from '@helix-ds/react'
import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useFamilyPortalApiClient } from '../features/auth/hooks/useFamilyPortalApiClient'
import { buildConsentConfigResponse, type ConsentConfigPayload } from '../features/consent/consentConfig'
import {
  answersFromSecondaryState,
  buildSecondaryDocumentContent,
  getSecondaryItems,
  getSecondaryTitle,
  resolveSecondarySectionTitle,
} from '../features/consent/consentOptions/consentOptionsUtils'
import type {
  AnswerMetadata,
  ConsentConfigItem,
  ConsentConfigResponse,
  ConsentContext,
  SecondaryConsentRow,
  SecondaryConsentStateResponse,
} from '../features/consent/consentOptions/types'
import {
  answerSubjectTabs,
  isAnswerMetadata,
  scopedStateKey,
  secondaryAnswerSubjects,
  subjectTabLabels,
  withSiblingAnswers,
  type SubjectTarget,
} from '../features/consent/consentSubjects'
import { track } from '../utils/analytics'
import { withSupportMessage } from '../utils/errorMessage'

type ProfileDemographics = {
  firstName?: string | null
  lastName?: string | null
}

type SubjectTab = {
  key: string
  name: string
  eyebrow: string
  /** Where this subject's answer for an item lives in `states` / `metadata`. */
  stateKey: (itemId: string) => string
  target: SubjectTarget
}

function formatUpdatedAt(value: string | null | undefined): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function displayNameFromParts(firstName?: string | null, lastName?: string | null): string | null {
  const name = [firstName, lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')
  return name || null
}

function firstParagraph(item: ConsentConfigItem): string {
  return item.descriptionParagraphs?.[0] ?? item.description ?? item.title
}

function ConsentChoiceButton({
  checked,
  disabled,
  label,
  onClick,
}: {
  checked: boolean
  disabled: boolean
  label: string
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      role="radio"
      aria-checked={checked}
      variant={checked ? 'primary' : 'secondary'}
      disabled={disabled}
      onClick={onClick}
      className="min-h-12 min-w-36 font-semibold"
      icon={
        <span className="flex h-5 w-5 items-center justify-center rounded-full border border-current" aria-hidden>
          {checked ? <span className="h-2.5 w-2.5 rounded-full bg-current" /> : null}
        </span>
      }
    >
      {label}
    </Button>
  )
}

function LoadingState() {
  return (
    <div className="mx-auto max-w-3xl py-16">
      <p className="text-text-body text-base" aria-live="polite">
        <FormattedMessage id="manageConsents.loading.message" defaultMessage="Loading consent preferences..." />
      </p>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="mx-auto max-w-3xl py-12">
      <Link to="/profile" className="text-brand-600 text-sm font-semibold underline">
        <FormattedMessage id="manageConsents.error.backToProfile" defaultMessage="Back to profile" />
      </Link>
      <div className="mt-6">
        <AlertBanner variant="error">{message}</AlertBanner>
      </div>
    </div>
  )
}

const ManageConsents: React.FC = () => {
  const intl = useIntl()
  const apiClient = useFamilyPortalApiClient()
  const { user } = useAuth0()
  const { addNotification } = useToast()
  const [context, setContext] = useState<ConsentContext | null>(null)
  const [config, setConfig] = useState<ConsentConfigResponse | null>(null)
  const [states, setStates] = useState<Record<string, boolean>>({})
  const [initialStates, setInitialStates] = useState<Record<string, boolean>>({})
  const [metadata, setMetadata] = useState<Record<string, AnswerMetadata>>({})
  const [demographics, setDemographics] = useState<ProfileDemographics | null>(null)
  const [activeSubjectKey, setActiveSubjectKey] = useState('single')
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<'context' | 'options' | 'preferences' | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setLoadError(null)
      try {
        const [contextRes, configRes, demographicsRes] = await Promise.all([
          apiClient.get('/consents/context', undefined, true),
          apiClient.get('/config/consents/secondary'),
          apiClient.get('/profile/demographics'),
        ])

        if (cancelled) return

        if (!contextRes.ok) {
          setLoadError('context')
          return
        }

        if (!configRes.ok) {
          setLoadError('options')
          return
        }

        const contextData = (await contextRes.json()) as ConsentContext
        const configPayload = (await configRes.json()) as ConsentConfigPayload
        const configData = buildConsentConfigResponse(configPayload, intl.locale)
        const demographicsData = demographicsRes.ok
          ? ((await demographicsRes.json().catch(() => null)) as ProfileDemographics | null)
          : null

        const params = new URLSearchParams({
          configId: configData.configId,
          configVersionId: configData.configVersionId,
          consentType: 'secondary',
        })
        const stateRes = await apiClient.get(`/consents/secondary?${params.toString()}`)
        const stateData = stateRes.ok
          ? ((await stateRes.json().catch(() => ({}))) as SecondaryConsentStateResponse)
          : {}

        if (cancelled) return

        const subjectsForAnswers = secondaryAnswerSubjects(
          contextData,
          contextData.secondaryConsentDualSubjects === true
        )
        const answers = answersFromSecondaryState(stateData, subjectsForAnswers).toggles
        setContext(contextData)
        setConfig(configData)
        setDemographics(demographicsData)
        setStates(answers)
        setInitialStates(answers)
        setMetadata(withSiblingAnswers(stateData.itemMetadata, stateData.subjectItemMetadata, isAnswerMetadata))
      } catch {
        if (!cancelled) setLoadError('preferences')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [apiClient, intl.locale])

  const secondaryItems = useMemo(() => (config ? getSecondaryItems(config) : []), [config])

  const patientName =
    context?.patientName?.trim() ||
    context?.patientFirstName?.trim() ||
    intl.formatMessage({ id: 'manageConsents.subject.patientFallback', defaultMessage: 'Patient' })
  const selfName =
    displayNameFromParts(demographics?.firstName, demographics?.lastName) ||
    user?.name ||
    user?.given_name ||
    intl.formatMessage({ id: 'manageConsents.subject.selfFallback', defaultMessage: 'You' })

  const subjects = useMemo<SubjectTab[]>(() => {
    const dual = context?.secondaryConsentDualSubjects === true
    const guardianEyebrow = intl.formatMessage({
      id: 'manageConsents.subject.dependentEyebrow',
      defaultMessage: 'Patient - You manage these choices as guardian',
    })
    const selfEyebrow = intl.formatMessage({
      id: 'manageConsents.subject.selfEyebrow',
      defaultMessage: 'Your preferences',
    })

    const tabs = answerSubjectTabs(context, dual)
    if (tabs) {
      return tabs.map(({ subject, ...tab }) => ({ ...tab, ...subjectTabLabels(subject, intl, context, selfName) }))
    }

    if (dual) {
      return [
        {
          key: 'dependent',
          name: patientName,
          eyebrow: guardianEyebrow,
          stateKey: (itemId: string) => scopedStateKey(itemId, 'DEPENDENT'),
          target: { subjectScope: 'DEPENDENT' },
        },
        {
          key: 'self',
          name: selfName,
          eyebrow: selfEyebrow,
          stateKey: (itemId: string) => scopedStateKey(itemId, 'SELF'),
          target: { subjectScope: 'SELF' },
        },
      ]
    }

    const isComparator =
      context?.primaryConsentCheckboxVariant === 'comparator' && context?.primaryConsentScenario !== 'proband_only'
    return [
      {
        key: 'single',
        stateKey: (itemId: string) => scopedStateKey(itemId),
        target: {},
        name: isComparator ? selfName : patientName,
        eyebrow: isComparator
          ? selfEyebrow
          : intl.formatMessage({
              id: 'manageConsents.subject.patientEyebrow',
              defaultMessage: 'Patient preferences',
            }),
      },
    ]
  }, [context, intl, patientName, selfName])

  useEffect(() => {
    if (!subjects.some((subject) => subject.key === activeSubjectKey)) {
      setActiveSubjectKey(subjects[0]?.key ?? 'single')
    }
  }, [activeSubjectKey, subjects])

  const activeSubject = subjects.find((subject) => subject.key === activeSubjectKey) ?? subjects[0]

  const sortedSecondaryItems = useMemo(() => {
    if (!activeSubject) return secondaryItems

    return secondaryItems
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const aOutstanding = initialStates[activeSubject.stateKey(a.row.item.id)] === undefined
        const bOutstanding = initialStates[activeSubject.stateKey(b.row.item.id)] === undefined

        if (aOutstanding !== bOutstanding) return aOutstanding ? -1 : 1
        return a.index - b.index
      })
      .map(({ row }) => row)
  }, [activeSubject, secondaryItems, initialStates])

  // Saves a single secondary consent preference for the active subject and rolls back on failure.
  async function savePreference(subject: SubjectTab, row: SecondaryConsentRow, value: boolean) {
    if (!config) return
    const key = subject.stateKey(row.item.id)
    const previousValue = states[key]
    if (previousValue === value || savingKey !== null) return
    const previousMetadata = metadata[key]
    const updatedAt = new Date().toISOString()

    setSavingKey(key)
    setStates((prev) => ({ ...prev, [key]: value }))
    setMetadata((prev) => ({ ...prev, [key]: { updatedAt } }))

    try {
      const res = await apiClient.post('/consents/secondary', {
        configId: config.configId,
        configVersionId: config.configVersionId,
        ...(typeof context?.clientConfigVersion === 'number' ? { version: String(context.clientConfigVersion) } : {}),
        consentType: 'secondary',
        entries: [
          {
            itemId: row.item.id,
            sectionTitle: resolveSecondarySectionTitle(row.sectionTitle),
            isConsented: value,
            election: value ? 'opt_in' : 'opt_out',
            ...subject.target,
          },
        ],
        documentContent: buildSecondaryDocumentContent(row.item, intl.locale),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        const message =
          typeof data?.detail === 'string'
            ? data.detail
            : intl.formatMessage({
                id: 'manageConsents.error.couldNotSavePreference',
                defaultMessage: "We couldn't save your preference. Please try again.",
              })
        throw new Error(message)
      }

      addNotification({
        type: 'success',
        title: intl.formatMessage({
          id: 'manageConsents.toast.preferenceUpdatedTitle',
          defaultMessage: 'Consent preference updated',
        }),
        message: intl.formatMessage(
          {
            id: 'manageConsents.toast.preferenceUpdatedMessage',
            defaultMessage: "{name}'s {title} preference is now {accepted, select, true {accepted} other {declined}}.",
          },
          {
            name: subject.name,
            title: getSecondaryTitle(row.item).toLowerCase(),
            accepted: String(value),
          }
        ),
      })
    } catch (error) {
      setStates((prev) => {
        const next = { ...prev }
        if (previousValue === undefined) {
          delete next[key]
        } else {
          next[key] = previousValue
        }
        return next
      })
      setMetadata((prev) => {
        const next = { ...prev }
        if (previousMetadata === undefined) {
          delete next[key]
        } else {
          next[key] = previousMetadata
        }
        return next
      })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'manageConsents.toast.couldNotSavePreferenceTitle',
          defaultMessage: "Couldn't save preference",
        }),
        message: withSupportMessage(
          intl,
          error instanceof Error
            ? error.message
            : intl.formatMessage({
                id: 'manageConsents.error.couldNotSavePreference',
                defaultMessage: "We couldn't save your preference. Please try again.",
              })
        ),
      })
    } finally {
      setSavingKey(null)
    }
  }

  if (loading) return <LoadingState />
  if (loadError || !context || !config) {
    const errorBodyKey: Record<'context' | 'options' | 'preferences', { id: string; defaultMessage: string }> = {
      context: {
        id: 'manageConsents.error.couldNotLoadContext',
        defaultMessage: "We couldn't load your consent context.",
      },
      options: {
        id: 'manageConsents.error.couldNotLoadOptions',
        defaultMessage: "We couldn't load your consent options.",
      },
      preferences: {
        id: 'manageConsents.error.couldNotLoadPreferencesData',
        defaultMessage: "We couldn't load your consent preferences.",
      },
    }
    const body = loadError
      ? intl.formatMessage(errorBodyKey[loadError])
      : intl.formatMessage({
          id: 'manageConsents.error.couldNotLoadPreferences',
          defaultMessage: "We couldn't load your preferences.",
        })
    return <ErrorState message={withSupportMessage(intl, body)} />
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-1 pb-12 sm:px-0">
      <section className="space-y-4">
        <Link to="/profile" className="text-brand-700 inline-flex text-sm font-semibold hover:underline">
          <FormattedMessage id="manageConsents.backToProfile" defaultMessage="Back to profile" />
        </Link>
        <div className="space-y-2">
          <h1 className="text-text-heading text-4xl font-semibold tracking-tight">
            <FormattedMessage id="manageConsents.heading" defaultMessage="Manage your consents" />
          </h1>
          <p className="text-text-body text-base">
            <FormattedMessage
              id="manageConsents.body"
              defaultMessage="Choose a family member, then update optional preferences. Changes are applied immediately."
            />
          </p>
        </div>
      </section>

      <section>
        <Card padding="none" className="overflow-hidden">
          {subjects.length > 1 && (
            <div className="border-border-base bg-background-primary flex gap-2 border-b px-4 pt-3">
              {subjects.map((subject) => (
                <button
                  key={subject.key}
                  type="button"
                  aria-pressed={activeSubjectKey === subject.key}
                  onClick={() => setActiveSubjectKey(subject.key)}
                  className={`rounded-t-xl border px-5 py-3 text-sm font-semibold transition ${
                    activeSubjectKey === subject.key
                      ? 'border-border-base border-b-background-primary bg-bg-secondary text-text-heading'
                      : 'text-text-body hover:text-text-heading border-transparent'
                  }`}
                >
                  {subject.name}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-8 p-5 sm:p-7">
            <div className="space-y-2">
              <p className="text-text-body text-sm font-medium">{activeSubject.eyebrow}</p>
              <h2 className="text-text-heading text-2xl font-semibold">{activeSubject.name}</h2>
            </div>

            <div className="border-border-base border-t">
              {sortedSecondaryItems.map((row) => {
                const stateKey = activeSubject.stateKey(row.item.id)
                const currentValue = states[stateKey]
                const updatedAt = formatUpdatedAt(metadata[stateKey]?.updatedAt)
                const disabled = savingKey !== null
                const isOpen = openItems[row.item.id] ?? false
                const hasLearnMore = (row.item.descriptionParagraphs?.length ?? 0) > 1
                const learnMoreParagraphs = row.item.descriptionParagraphs?.slice(1) ?? []

                return (
                  <section key={stateKey} className="border-border-base space-y-4 border-b py-7 last:border-b-0">
                    <div className="space-y-3">
                      <h3 className="text-text-heading text-base font-semibold">{getSecondaryTitle(row.item)}</h3>
                      <p className="text-text-body text-2xl leading-snug">{firstParagraph(row.item)}</p>
                      {hasLearnMore && (
                        <div>
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-controls={`learn-more-${row.item.id}`}
                            onClick={() => {
                              const next = !isOpen
                              setOpenItems((prev) => ({ ...prev, [row.item.id]: next }))
                              track(next ? 'consent.section_expanded' : 'consent.section_collapsed', {
                                item_id: row.item.id,
                              })
                            }}
                            className="text-brand-700 dark:text-brand-400 focus-visible:outline-brand-600 text-sm font-medium underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
                          >
                            {intl.formatMessage(
                              isOpen
                                ? { id: 'manageConsents.showLess', defaultMessage: 'Show less' }
                                : { id: 'manageConsents.learnMore', defaultMessage: 'Learn more' }
                            )}
                          </button>
                          <div
                            id={`learn-more-${row.item.id}`}
                            className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                            style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                          >
                            <div className="overflow-hidden" aria-hidden={!isOpen}>
                              <div className="space-y-3 pt-3">
                                {learnMoreParagraphs.map((p, i) => (
                                  <p key={i} className="text-text-body text-sm leading-6">
                                    {p}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      role="radiogroup"
                      aria-label={intl.formatMessage(
                        { id: 'manageConsents.radiogroup.ariaLabel', defaultMessage: '{title} preference' },
                        { title: getSecondaryTitle(row.item) }
                      )}
                      className="flex flex-wrap gap-3"
                    >
                      <ConsentChoiceButton
                        label={intl.formatMessage({ id: 'manageConsents.choice.accept', defaultMessage: 'Accept' })}
                        checked={currentValue === true}
                        disabled={disabled}
                        onClick={() => savePreference(activeSubject, row, true)}
                      />
                      <ConsentChoiceButton
                        label={intl.formatMessage({ id: 'manageConsents.choice.decline', defaultMessage: 'Decline' })}
                        checked={currentValue === false}
                        disabled={disabled}
                        onClick={() => savePreference(activeSubject, row, false)}
                      />
                    </div>

                    <p className="text-text-subtle text-sm">
                      {updatedAt
                        ? intl.formatMessage(
                            { id: 'manageConsents.updatedAt', defaultMessage: 'Last updated {updatedAt}' },
                            { updatedAt }
                          )
                        : intl.formatMessage({ id: 'manageConsents.notUpdatedYet', defaultMessage: 'Not updated yet' })}
                    </p>
                  </section>
                )
              })}
            </div>
          </div>
        </Card>
      </section>
    </div>
  )
}

export default ManageConsents
